import React from 'react';
import { Box, Switch, TextField, MenuItem, Select } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid'; 
import { useData, useDataFunctions } from 'contexts/Data';

/**
 * Example data shape:
 * [
 *   {
 *     email: "iblum@prologis.com",
 *     title: "user:user:iblum@prologis.com",
 *     last_active: "2025-03-19T20:54:12.891551",
 *     roles: ["ADMIN"],
 *     nickname: "",
 *     notes: "",
 *   }
 * ]
 */

// Placeholder: fetch all roles from backend or a pre-defined list
const fetchAllRoles = async () => {
  return ["ADMIN", "USER", "MANAGER", "READ_ONLY"];
};

export default function UserManager() {
  const data = useData(); // might hold general global data
  const dataFunctions = useDataFunctions(); // might have getUsers, updateUser, etc.

  const [users, setUsers] = React.useState([]);
  const [allRoles, setAllRoles] = React.useState([]);

  React.useEffect(() => {
    // Get roles from backend or static
    fetchAllRoles().then((roles) => setAllRoles(roles));

    // Get users from your backend
    dataFunctions
      .getUsers()
      .then((result) => {
        setUsers(result.users);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  /**
   * Triggered when a user finishes editing a cell. 
   * We'll call an imaginary 'updateUser' function on the backend.
   */
  const handleRowEditCommit = React.useCallback(
    async (params) => {
      const { id, field, value } = params;
      // 'id' here is the row identifier (based on getRowId)
      // 'field' is the column name (e.g. 'nickname', 'notes', or 'roles')
      // 'value' is the updated cell value

      // Step 1: Find the user in local state
      const updatedUsers = [...users];
      const idx = updatedUsers.findIndex((u) => u.email === id);
      if (idx === -1) return;

      // Step 2: Update the local data with new value
      updatedUsers[idx] = { ...updatedUsers[idx], [field]: value };
      setUsers(updatedUsers);

      // Step 3: Call your backend
      // Just a placeholder function to demonstrate updating the server
      // You can adapt it to your actual dataFunctions.updateUser or similar
      try {
        await dataFunctions.updateUser({ email: id, [field]: value });
        console.log("User updated successfully.");
      } catch (error) {
        console.error("Failed to update user:", error);
      }
    },
    [users, dataFunctions]
  );

  // Helper to check if user has ADMIN role
  const isAdmin = (roles) => roles && roles.includes("ADMIN");

  /**
   * If you’d like to do a one-click “Set as admin” or remove admin, 
   * you could do that in a separate function:
   */
  const handleAdminToggle = (email, newValue) => {
    // newValue is true if turning admin on, false if off
    // This is a placeholder. Make a real call to your backend as needed.
    console.log(`Toggling admin for ${email} to: `, newValue);
    // Then update user state here.
  };

  /**
   * Configure columns for the DataGrid.
   * - 'editable: true' allows direct editing in the table.
   * - 'renderCell' or 'renderEditCell' can handle custom editors (e.g. for role multi-select).
   * - 'flex' helps with responsive column sizing.
   */
  const columns = [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      editable: false, // Usually the primary key or unique ID isn't editable
    },
    // {
    //   field: 'title',
    //   headerName: 'Title',
    //   flex: 1,
    //   editable: true,
    // },
    {
      field: 'last_active',
      headerName: 'Last Active',
      flex: 1,
      editable: false, // Typically read-only
    },
    {
      field: 'roles',
      headerName: 'Roles',
      flex: 1,
      editable: true,
      renderEditCell: (params) => {
        // Using a Multi-Select drop-down or single select drop-down
        // For demonstration, let's do a single select. 
        // For multiple, you'd want to use something like <Autocomplete multiple .../>.
        return (
          <Select
            value={params.value?.[0] || ''} // If multiple roles, pick the first, for example
            onChange={(e) => {
              // e.target.value could be a single role or array if you want multi
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: [e.target.value], // storing as array
              });
            }}
            autoFocus
            fullWidth
          >
            {allRoles.map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </Select>
        );
      },
      // Example: or show a simple comma join
      renderCell: (params) => <>{params.value?.join(', ')}</>,
    },
    {
      field: 'nickname',
      headerName: 'Nickname',
      flex: 1,
      editable: true,
      // Data might be missing initially, so handle that gracefully
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 2,
      editable: true,
      renderEditCell: (params) => (
        <TextField
          multiline
          rows={3}
          value={params.value || ''}
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value,
            })
          }
          fullWidth
          autoFocus
        />
      ),
    },
    // {
    //   field: 'adminToggle',
    //   headerName: 'Admin?',
    //   // We don't store it directly, but infer from roles
    //   valueGetter: (params) => isAdmin(params.row.roles),
    //   renderCell: (params) => {
    //     const checked = params.value; // a boolean from valueGetter
    //     return (
    //       <Switch
    //         checked={!!checked}
    //         onChange={(e) => {
    //           handleAdminToggle(params.row.email, e.target.checked);
    //         }}
    //       />
    //     );
    //   },
    //   sortable: false,
    //   filterable: false,
    //   width: 120,
    //   align: 'center',
    //   headerAlign: 'center',
    //   editable: false,
    // },
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      {/* <h1>User Manager</h1> */}
      <DataGrid
        rows={users}
        columns={columns}
        getRowId={(row) => row.email} 
        
        // Enable built-in toolbar with search/filter/sort etc.
        components={{ Toolbar: GridToolbar }}
        
        // If you want row editing mode:
        editMode="row"
        onRowEditCommit={handleRowEditCommit}
        
        // Basic DataGrid props
        pageSize={10}
        rowsPerPageOptions={[5, 10, 25]}
        autoHeight
      />
    </Box>
  );
}
